import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { formatMoney } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { registrationApi } from '@/services/registration.api';
import { Button, EmptyState, Spinner } from '@/components/ui';
import { ModulePicker, totalMonthlyCents } from '@/components/module-picker';

/**
 * Module selection — the registration wizard's first step.
 *
 * A church builds its own bundle from the purchasable catalog
 * (`GET /modules/catalog`, module-registry-service's pre-auth surface). The
 * free baseline — core modules plus `PEOPLE` — is never a choice, so it's
 * never mixed into that grid; it gets its own collapsed panel above it
 * instead, there for a prospective church to check but out of the way of
 * the actual decision.
 */
export function ModulesStep({
  selectedModuleKeys,
  onChange,
  onNext,
}: {
  readonly selectedModuleKeys: readonly string[];
  readonly onChange: (moduleKeys: string[]) => void;
  readonly onNext: (totalCents: number) => void;
}): ReactNode {
  const { t, locale } = useI18n();
  const query = useQuery({
    queryKey: ['registration', 'modules'],
    queryFn: () => registrationApi.listModuleCatalog(),
  });

  const catalog = query.data ?? [];
  const includedModules = catalog.filter((entry) => entry.isBaseline);
  const shopModules = catalog.filter((entry) => !entry.isBaseline);
  const totalCents = totalMonthlyCents(catalog, selectedModuleKeys);

  return (
    <div>
      <h2 className="auth-title">{t('registration.modules.title')}</h2>
      <p className="auth-subtitle">{t('registration.modules.subtitle')}</p>

      {query.isLoading && <Spinner label={t('common.states.loading')} />}
      {query.isError && <EmptyState title={t('registration.modules.loadError')} />}

      {includedModules.length > 0 && (
        <details className="people-drawer-section">
          <summary>
            {t('registration.modules.includedTitle', {
              values: { count: includedModules.length },
            })}
          </summary>
          <div className="people-drawer-section-body">
            <div className="plan-grid">
              {includedModules.map((entry) => (
                <div key={entry.key} className="plan-card" data-selected="true">
                  <span className="plan-card-name">{entry.title}</span>
                  <span className="plan-card-tagline">{entry.description}</span>
                  <span className="plan-card-trial">{t('platform.modules.included')}</span>
                </div>
              ))}
            </div>
          </div>
        </details>
      )}

      <ModulePicker
        catalog={shopModules}
        selectedModuleKeys={selectedModuleKeys}
        onChange={onChange}
      />

      {query.data && (
        <div className="plan-footer">
          <span className="plan-footer-total">
            {totalCents > 0
              ? t('registration.modules.total', {
                  values: { amount: formatMoney(totalCents, 'USD', locale) },
                })
              : t('registration.modules.totalFree')}
          </span>
          <Button type="button" variant="primary" onClick={() => onNext(totalCents)}>
            {t('common.actions.next')}
          </Button>
        </div>
      )}
    </div>
  );
}
